import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@api/client";

export type ApprovalExtractionFieldOperator =
  | "eq"
  | "neq"
  | "contains"
  | "not_contains"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "exists"
  | "not_exists";

export interface ApprovalExtractionFieldCondition {
  field: string;
  operator: ApprovalExtractionFieldOperator;
  value?: string | number | boolean | null;
}

export type ApprovalChainConditions = Record<string, any> & {
  extraction_field_conditions?: ApprovalExtractionFieldCondition[];
};

export interface ApprovalChainStep {
  id?: string;
  step_number: number;
  approvers_type: string;
  approver_ids?: string[];
  approver_roles?: string[];
  manager_level?: number;
  org_unit_id?: string;
  parallel_approval: boolean;
  required_approvals: number;
  escalation_minutes?: number;
  auto_approve_after_minutes?: number;
  escalate_after_hours?: number;
  auto_approve_after_hours?: number;
  required_role?: string;
  required_org_unit_type?: string;
  assigned_user_id?: string;
  manager_approval?: boolean;
}

export interface CreateApprovalChainRequest {
  name: string;
  description?: string;
  action_type: string;
  entity_type: string;
  document_type?: string;
  conditions: ApprovalChainConditions;
  steps: ApprovalChainStep[];
  is_enabled: boolean;
  is_active: boolean;
}

export interface ApprovalChain {
  id: string;
  name: string;
  description?: string;
  action_type: string;
  entity_type: string;
  document_type?: string;
  conditions: ApprovalChainConditions;
  steps: ApprovalChainStep[];
  is_enabled: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface UseApprovalChainsReturn {
  createChain: (data: CreateApprovalChainRequest) => Promise<ApprovalChain>;
  updateChain: (
    chainId: string,
    data: CreateApprovalChainRequest,
  ) => Promise<ApprovalChain>;
  deleteChain: (chainId: string) => Promise<void>;
  getChain: (chainId: string) => Promise<ApprovalChain>;
  listChains: (filters?: {
    action_type?: string;
    entity_type?: string;
    is_enabled?: boolean;
  }) => Promise<ApprovalChain[]>;
  loading: boolean;
  error: Error | null;
}

export const useApprovalChains = (): UseApprovalChainsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const queryClient = useQueryClient();

  const createChain = useCallback(
    async (data: CreateApprovalChainRequest): Promise<ApprovalChain> => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.post("/approval-chains", data);
        const createdChain = response.data;

        queryClient.setQueryData(["approval-chains"], (currentData: any) => {
          const chainListEntry = {
            ...createdChain,
            step_count:
              createdChain.step_count ?? createdChain.steps?.length ?? 0,
            priority: createdChain.priority ?? 0,
          };

          if (Array.isArray(currentData)) {
            return [chainListEntry, ...currentData];
          }

          if (Array.isArray(currentData?.chains)) {
            return {
              ...currentData,
              chains: [chainListEntry, ...currentData.chains],
            };
          }

          return currentData;
        });
        await queryClient.invalidateQueries({ queryKey: ["approval-chains"] });

        return createdChain;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to create approval chain");
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [queryClient],
  );

  const updateChain = useCallback(
    async (
      chainId: string,
      data: CreateApprovalChainRequest,
    ): Promise<ApprovalChain> => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.put(
          `/approval-chains/${chainId}`,
          data,
        );
        const updatedChain = response.data as ApprovalChain;

        queryClient.setQueryData(["approval-chain", chainId], updatedChain);
        await queryClient.invalidateQueries({
          queryKey: ["approval-chain", chainId],
        });
        await queryClient.invalidateQueries({ queryKey: ["approval-chains"] });

        return updatedChain;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to update approval chain");
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [queryClient],
  );

  const deleteChain = useCallback(
    async (chainId: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        await apiClient.delete(`/approval-chains/${chainId}`);
        await queryClient.invalidateQueries({ queryKey: ["approval-chains"] });
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to delete approval chain");
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [queryClient],
  );

  const getChain = useCallback(
    async (chainId: string): Promise<ApprovalChain> => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get(`/approval-chains/${chainId}`);
        return response.data;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to fetch approval chain");
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const listChains = useCallback(
    async (filters?: {
      action_type?: string;
      entity_type?: string;
      is_enabled?: boolean;
    }): Promise<ApprovalChain[]> => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (filters?.action_type)
          params.append("action_type", filters.action_type);
        if (filters?.entity_type)
          params.append("entity_type", filters.entity_type);
        if (filters?.is_enabled !== undefined)
          params.append("is_enabled", String(filters.is_enabled));

        const response = await apiClient.get(
          `/approval-chains${params.toString() ? `?${params.toString()}` : ""}`,
        );
        return Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to list approval chains");
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createChain,
    updateChain,
    deleteChain,
    getChain,
    listChains,
    loading,
    error,
  };
};
